<template>
  <!--
    Every dismiss in the application is this button, so they all behave the
    same: a modal, a toast, a side panel, a row you are removing. Before this
    each one drew its own × and picked its own size and hover.

    The cross is drawn, not typed. A × character sits off-centre in most fonts
    and lands at a different weight than the stroked icons beside it.
  -->
  <button
    type="button"
    class="close"
    :class="[`close--${size}`, { 'close--danger': danger }]"
    :aria-label="label ?? uiLabel('close')"
    @click="$emit('click', $event)"
  >
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  </button>
</template>

<script setup>
import { uiLabel } from '../utils/labels'

defineProps({
  size: { type: String, default: 'md' },   // sm | md
  label: String,
  // For a close that destroys something rather than just hiding it.
  danger: Boolean,
})
defineEmits(['click'])
</script>

<style scoped>
.close {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-mute);
  /* The rotation is the whole point: a quarter turn, eased, so the cross
     settles rather than snapping. Slow enough to notice, short enough that it
     is never in the way of the click. */
  transition: transform 260ms var(--ease), color var(--fast), background var(--fast);
}

.close--md { width: 30px; height: 30px; }
.close--sm { width: 24px; height: 24px; }

.close svg { width: 60%; height: 60%; }

.close:hover,
.close:focus-visible {
  transform: rotate(90deg);
  color: var(--color-text);
  background: var(--color-gray-100);
}

.close--danger:hover,
.close--danger:focus-visible {
  color: var(--color-danger);
  background: var(--color-danger-soft);
}

/* Same reasoning as the buttons: a cross is the smallest thing on the screen
   and the one people miss. The drawn cross keeps its proportion, so it grows
   with the box rather than sitting small inside a large one. */
@media (pointer: coarse) {
  .close--md { width: 40px; height: 40px; }
  .close--sm { width: 34px; height: 34px; }
}

/* Los dos tamaños llegan a 44 con el dedo, y a propósito quedan iguales: `sm` y
   `md` son una decisión de DENSIDAD para un ratón, y con el dedo no hay dos
   densidades — hay un mínimo. La pequeña salía en 24 pantallas a 34x34, y es la
   cruz de un modal: fallarla es quedarse dentro. */
@media (pointer: coarse) {
  .close--md,
  .close--sm { width: 44px; height: 44px; }
}

/* Pressing it settles back a touch, so the button feels physical. */
.close:active { transform: rotate(90deg) scale(0.92); }

/* Somebody who asked for less motion gets the colour change and nothing else;
   the global reduced-motion rule already kills the duration, this drops the
   turn as well so it does not jump straight to 90 degrees. */
@media (prefers-reduced-motion: reduce) {
  .close:hover, .close:focus-visible, .close:active { transform: none; }
}
</style>
